import { Component, Inject, OnInit } from "@angular/core";
import { v4 } from "uuid";
import {
  ToastController,
  NavController,
  ViewController,
  NavParams,
  App
} from "ionic-angular";
import { AuthService } from "../../app/services/auth.service";
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl
} from "@angular/forms";
import {
  diabetesTypeOptions,
  bloodSugarMedicineOptions,
  danishMunicipalities,
  patientConsentToSmsOptions,
  patientDeactivationReasonOptions
} from "../../app/models/types";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs/Subscription";
import { AppState } from "../../app/models/app.state";
import {
  DiaprofilPatient,
  IDiaprofilPatient,
  diaprofilPatientFactory
} from "../../app/models/diaprofil-patient";
import { FhirService } from "../../app/services/fhir.service";
import * as PatientActions from "../../app/store/actions/patient.actions";
import { cprValidator } from "../../app/validators/cpr-validator.directive";
import { CPR } from "../../app/models/cpr";
import {
  AdminService,
  ICprLookupResult
} from "../../app/services/admin.service";
import { OnDestroy } from "@angular/core/src/metadata/lifecycle_hooks";
import { notNullOrUndefined } from "../../app/utils/not-null-or-undefined";
import {
  ENV_VARS,
  EnvironmentVariables
} from "../../app/environment-variables/environment-variables.module";
import { generatePassword } from "../../app/utils/generate-password";
import { _i18n_str } from "../../assets/i18n/non-static-ids";
import { ModalController } from "ionic-angular/components/modal/modal-controller";
import { TranslateService } from "@ngx-translate/core";
import { QuestionnaireResponseService } from "@zitelab/ionic-fhir-questionnaires/dist/index";
import { DashboardService } from "../../app/services/dashboard.service";
import { PatientPage } from "../../pages/patient.page/patient.page";
import { AlertController } from "ionic-angular/components/alert/alert-controller";
import { LoadingController } from "ionic-angular/components/loading/loading-controller";

export function uuid(): string {
  return v4().replace(/-/g, "");
}

@Component({
  selector: "manage-patient-modal",
  templateUrl: "manage-patient.modal.html"
})
export class ManagePatientModal implements OnInit, OnDestroy {
  userForm: FormGroup;
  modalType: "EDIT" | "CREATE";
  patient: DiaprofilPatient;
  diabetesTypeOptions = diabetesTypeOptions;
  bloodSugarMedicineOptions = bloodSugarMedicineOptions;
  patientConsentToSmsOptions = patientConsentToSmsOptions;
  danishMunicipalities = danishMunicipalities;
  deactivationReasons = patientDeactivationReasonOptions;
  userNameAndPasswordEnabled: boolean = false;
  _i18n_str = _i18n_str;
  anyMiscFieldsRequired: boolean = false;
  showMiscFields: boolean = false;
  orgConfigSub: Subscription;
  loading: boolean = true;
  constructor(
    public navCtrl: NavController,
    public viewCtrl: ViewController,
    private navParams: NavParams,
    public auth: AuthService,
    public fhir: FhirService,
    public admin: AdminService,
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
    private fb: FormBuilder,
    private app: App,
    private store: Store<AppState>,
    private modalCtrl: ModalController,
    private loadingCtrl: LoadingController,
    public translate: TranslateService,
    public questionnaireResponseService: QuestionnaireResponseService,
    public dashboard: DashboardService,
    @Inject(ENV_VARS) public envVars: EnvironmentVariables
  ) {
    this.userNameAndPasswordEnabled =
      this.envVars.ENABLE_USER_NAME_PASSWORD_FOR_PATIENT_CREATION === "YES";
  }

  ngOnInit() {}

  ionViewWillEnter() {
    this.auth.setOrganizationConfig().subscribe(orgConfig => {
      this.modalType = this.navParams.get("modalType") || "CREATE";
      this.patient =
        (this.navParams.get("patient") as DiaprofilPatient) ||
        <DiaprofilPatient>{};
      const prefilled =
        (this.navParams.get("prefilled") as { ssn: string; name: string }) ||
        null;

      this.anyMiscFieldsRequired =
        [
          orgConfig.patientSchemaMedicineTypesRequired,
          orgConfig.patientSchemaMunicipalityRequired,
          orgConfig.patientSchemaDiagnosisYearRequired
        ].filter(x => x === true).length > 0;
      this.showMiscFields = this.anyMiscFieldsRequired;

      /* Setup the form */
      this.userForm = this.fb.group({
        fullName: [
          prefilled !== null ? prefilled.name : this.patient.fullName,
          Validators.compose([Validators.required, Validators.minLength(2)])
        ],
        municipality: [
          this.modalType === "CREATE"
            ? orgConfig.patientSchemaMunicipalityDefault
            : this.patient.municipality,
          Validators.compose(
            orgConfig.patientSchemaMunicipalityRequired
              ? [Validators.required]
              : []
          )
        ],
        ssn: [
          prefilled !== null
            ? prefilled.ssn
            : this.patient.socialSecurityNumber,
          Validators.compose([Validators.required, cprValidator()])
        ],
        addressLineOne: [
          notNullOrUndefined(this.patient.address) &&
          this.patient.address.line.length > 0
            ? this.patient.address.line[0]
            : null,
          Validators.compose([Validators.required])
        ],
        addressLineTwo: [
          notNullOrUndefined(this.patient.address) &&
          this.patient.address.line.length > 1
            ? this.patient.address.line[1]
            : null,
          null
        ],
        city: [
          notNullOrUndefined(this.patient.address)
            ? this.patient.address.city
            : null,
          Validators.compose([Validators.required])
        ],
        postalCode: [
          notNullOrUndefined(this.patient.address)
            ? this.patient.address.postalCode
            : null,
          Validators.compose([
            Validators.required,
            Validators.pattern("^\\d{4}$")
          ])
        ],
        mobileNumber: [
          this.patient.mobileNumber,
          Validators.compose([
            Validators.pattern(
              /^((\(?\+45\)?)?)(\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2})$/
            )
          ])
        ],
        diagnosis: [
          this.modalType === "CREATE"
            ? orgConfig.patientSchemaDiabetesTypeDefault
            : this.patient.diagnosis,
          Validators.compose([
            ...(orgConfig.patientSchemaDiabetesTypeRequired
              ? [Validators.required]
              : [])
          ])
        ],
        diagnosisYear: [
          this.patient.diagnosisYear,
          Validators.compose([
            Validators.min(1920),
            Validators.max(new Date().getFullYear()),
            ...(orgConfig.patientSchemaDiagnosisYearRequired
              ? [Validators.required]
              : [])
          ])
        ],
        bloodSugarMedicineTypes: [
          this.modalType === "CREATE"
            ? orgConfig.patientSchemaMedicineTypesDefault
            : this.patient.bloodSugarMedicineTypes,
          Validators.compose(
            orgConfig.patientSchemaMedicineTypesRequired
              ? [Validators.required]
              : []
          )
        ]
      });
      if (orgConfig.patientSchemaConsentToSendSmsEnabled) {
        this.userForm.addControl(
          "consentToSms",
          new FormControl(
            this.modalType === "CREATE"
              ? "NOT_ANSWERED"
              : this.patient.consentToSms,
            Validators.compose([Validators.required])
          )
        );
      }
      if (this.modalType === "CREATE" && this.userNameAndPasswordEnabled) {
        this.userForm.addControl(
          "userName",
          new FormControl(
            null,
            Validators.compose([
              Validators.required,
              Validators.pattern("^[a-zA-Z][a-zA-Z0-9-_.]{5,20}$")
            ])
          )
        );
        this.userForm.addControl(
          "password",
          new FormControl(
            null,
            Validators.compose([Validators.required, Validators.minLength(6)])
          )
        );
      }
      this.loading = false;
    });
  }

  createFhirAddress(
    addressLineOne: string,
    addressLineTwo: string,
    city: string,
    postalCode: string
  ): fhir.Address {
    const fhirAddress: fhir.Address = {
      use: "home",
      type: "both",
      line: [addressLineOne],
      city: city,
      postalCode: postalCode,
      country: "DK"
    };
    if (addressLineTwo !== null && addressLineTwo !== "") {
      fhirAddress.line.push(addressLineTwo);
    }
    return fhirAddress;
  }

  createFhirTelecomForMobileNumber(mobileNumber): fhir.ContactPoint {
    const fhirAddress: fhir.ContactPoint = {
      system: "phone",
      use: "mobile",
      value: mobileNumber
    };
    return fhirAddress;
  }

  async createPatient() {
    const formValues = this.userForm.value;
    const cpr = new CPR(formValues.ssn);
    const patientUuid = uuid();
    const userName = this.userNameAndPasswordEnabled
      ? formValues.userName
      : patientUuid;
    const loading = this.loadingCtrl.create();
    let mobileNumber = formValues.mobileNumber;
    if (mobileNumber === "") {
      mobileNumber = null;
    }
    let organizationUuid = this.auth.userState.organizationUuid;
    if (organizationUuid === null || organizationUuid === undefined) {
      organizationUuid = this.auth.orgConfig.orgResource.id;
    }
    loading.present();
    this.fhir
      .createUser({
        username: userName,
        password: this.userNameAndPasswordEnabled
          ? formValues.password
          : generatePassword(),
        social_security_number: formValues.ssn,
        mobile_number: mobileNumber,
        email: `${patientUuid}@zitelab.dk`,
        fullname: formValues.fullName,
        user_type: "Patient",
        user_uid: patientUuid,
        organization_uid: organizationUuid,
        gender: cpr.sex.toLowerCase(),
        birthdate: cpr.birthdate.toISOString(),
        diagnosis: formValues.diagnosis,
        address: [
          this.createFhirAddress(
            formValues.addressLineOne,
            formValues.addressLineTwo,
            formValues.city,
            formValues.postalCode
          )
        ],
        telecom:
          formValues.mobileNumber !== "" && formValues.mobileNumber !== null
            ? [this.createFhirTelecomForMobileNumber(formValues.mobileNumber)]
            : [],
        properties: {
          municipality: formValues.municipality,
          // We don't want to submit a diagnosis year if we have no value - since it'll trigger a '0' value in Plone
          diagnosisYear:
            formValues.diagnosisYear !== null && formValues.diagnosisYear !== ""
              ? String(formValues.diagnosisYear)
              : undefined,
          bloodSugarMedicineTypes: formValues.bloodSugarMedicineTypes,
          consentToSms: this.admin.orgConfig
            .patientSchemaConsentToSendSmsEnabled
            ? formValues.consentToSms
            : undefined
        }
      })
      .subscribe(
        data => {
          const extraAction = result => {
            result = result.find(elem => elem.id === userName);
            this.viewCtrl.dismiss();
            const toast = this.toastCtrl.create({
              message: `Patient oprettet!`,
              duration: 5000,
              position: "top",
              closeButtonText: "Luk",
              showCloseButton: true
            });
            const patientObj: IDiaprofilPatient = {
              fhirId: result.fhir_id,
              userName: result.username,
              fhirResource: result.user_resource as fhir.Patient,
              socialSecurityNumber: result.social_security_number,
              fullName: result.fullname,
              homePath: result.home_path,
              language: result.language,
              mobileNumber: result.mobile_number,
              organizationUid: result.organization_uid,
              bloodSugarMedicineTypes: result.bloodSugarMedicineTypes,
              consentToSms: result.consentToSms,
              diagnosis: result.diagnosis,
              diagnosisYear: result.diagnosisYear,
              evalConsent: result.evalConsent,
              evalConsentResponseUuid: result.evalConsentResponseUuid,
              municipality: result.municipality,
              isDeactivated: result.isDeactivated,
              deactivationReason: result.deactivationReason,
              deactivationTimestamp: result.deactivationTimestamp,
              evalStudyParticipantId: result.evalStudyParticipantId,
              proTasks: [],
              questionnaireResponses: [],
              consultations: [],
              activeTask: null
            };
            this.store.dispatch(new PatientActions.ReplaceAction(patientObj));
            const createdPatient = diaprofilPatientFactory(
              patientObj,
              this.translate,
              this.questionnaireResponseService,
              this.dashboard
            );
            const rootNav = this.app.getRootNav();
            loading.dismissAll();
            rootNav.push(PatientPage, { patient: createdPatient });
            toast.present();
          };
          this.admin.getUsers({
            organizationUuid: this.auth.userState.organizationUuid,
            userType: "Patient",
            extraAction
          });
        },
        // Errors will call this callback instead:
        err => {
          loading.dismissAll();
          const toast = this.toastCtrl.create({
            message: this.transformErrorMessage(err),
            duration: 20000,
            position: "top",
            closeButtonText: "Luk",
            showCloseButton: true
          });
          toast.present();
        }
      );
  }

  patchPatient() {
    const formValues = this.userForm.value;
    const cpr = new CPR(formValues.ssn);
    this.fhir
      .patchUser(this.patient.userName, {
        social_security_number: formValues.ssn,
        fullname: formValues.fullName,
        mobile_number: formValues.mobileNumber,
        diagnosis: formValues.diagnosis,
        gender: cpr.sex.toLowerCase(),
        birthdate: cpr.birthdate.toISOString(),
        address: [
          this.createFhirAddress(
            formValues.addressLineOne,
            formValues.addressLineTwo,
            formValues.city,
            formValues.postalCode
          )
        ],
        telecom:
          formValues.mobileNumber !== "" && formValues.mobileNumber !== null
            ? [this.createFhirTelecomForMobileNumber(formValues.mobileNumber)]
            : [],
        // NOTE: Unlike for POST requests, we don't need to nest the user properties inside a properties key.
        municipality: formValues.municipality,
        // We don't want to submit a diagnosis year if we have no value - since it'll trigger a '0' value in Plone
        diagnosisYear:
          formValues.diagnosisYear !== null
            ? String(formValues.diagnosisYear)
            : undefined,
        bloodSugarMedicineTypes: formValues.bloodSugarMedicineTypes,
        consentToSms: this.admin.orgConfig.patientSchemaConsentToSendSmsEnabled
          ? formValues.consentToSms
          : undefined
      })
      .subscribe(
        result => {
          const updatedFhirResource = this.patient.fhirResource;
          updatedFhirResource.address = [
            this.createFhirAddress(
              formValues.addressLineOne,
              formValues.addressLineTwo,
              formValues.city,
              formValues.postalCode
            )
          ];
          updatedFhirResource.telecom = [
            this.createFhirTelecomForMobileNumber(formValues.mobileNumber)
          ];
          this.store.dispatch(
            new PatientActions.UpdateAction({
              fhirId: this.patient.fhirId,
              fullName: formValues.fullName,
              mobileNumber: formValues.mobileNumber,
              socialSecurityNumber: formValues.ssn,
              diagnosis: formValues.diagnosis,
              municipality: formValues.municipality,
              diagnosisYear: String(formValues.diagnosisYear),
              bloodSugarMedicineTypes: formValues.bloodSugarMedicineTypes,
              consentToSms: this.admin.orgConfig
                .patientSchemaConsentToSendSmsEnabled
                ? formValues.consentToSms
                : undefined,
              fhirResource: updatedFhirResource
            })
          );
          this.viewCtrl.dismiss();
          const toast = this.toastCtrl.create({
            message: `Patient opdateret!`,
            duration: 5000,
            position: "top",
            closeButtonText: "Luk",
            showCloseButton: true
          });
          toast.present();
        },
        // Errors will call this callback instead:
        err => {
          const toast = this.toastCtrl.create({
            message: this.transformErrorMessage(err),
            duration: 20000,
            position: "top",
            closeButtonText: "Luk",
            showCloseButton: true
          });
          toast.present();
        }
      );
  }

  transformErrorMessage(httpError: {
    error: {
      error: {
        type?: string;
        message?: string;
        errors: { field?: string; message?: string }[];
      };
    };
  }) {
    console.log(httpError);
    if (
      httpError === undefined ||
      httpError === null ||
      !httpError.error.hasOwnProperty("error")
    ) {
      return JSON.stringify(httpError);
    }
    if (
      httpError.error.error.type !== "FieldError" ||
      !httpError.error.error.hasOwnProperty("errors")
    ) {
      return JSON.stringify(httpError.error);
    }
    const ssnError = httpError.error.error.errors.find(
      elem => elem.field === "social_security_number"
    );
    if (ssnError !== undefined) {
      return ssnError.message.startsWith(
        "The provided social security number is already in provided hospital"
      )
        ? this.translate.instant(
            "MANAGE_PATIENT_MODAL.ERRORS.SSN_ALREADY_EXISTS"
          )
        : ssnError.message;
    }
    return JSON.stringify(httpError.error.error.errors);
  }

  patientStatusAlert() {
    const alert = this.alertCtrl.create({
      cssClass: "large-alert",
      title: "Patient status",
      message: "Angiv om patienten er aktiv eller afsluttet",
      inputs: [
        {
          type: "radio",
          name: "deactivationReason",
          value: "ACTIVE",
          label: "Aktiv",
          checked: true
        },
        ...this.deactivationReasons.map(reason => {
          return {
            type: "radio",
            name: "deactivationReason",
            value: reason.id,
            label: this.translate.instant(reason.i18n),
            checked: this.patient.deactivationReason === reason.id
          };
        })
      ],
      buttons: [
        {
          text: "Annullér",
          handler: data => {
            //
          }
        },
        {
          text: "Gem",
          handler: reasonCode => {
            this.deactivatePatient(event, reasonCode);
          }
        }
      ]
    });
    alert.present();
  }

  deactivatePatient(event, reasonCode) {
    if (reasonCode === "ACTIVE") {
      return;
    }
  }

  dismiss() {
    this.viewCtrl.dismiss();
  }

  ngOnDestroy() {
    try {
      this.orgConfigSub.unsubscribe();
    } catch (e) {}
  }

  toggleMiscFields() {
    this.showMiscFields = !this.showMiscFields;
  }

  async handleSubmit() {
    const phoneSoftRequired = this.admin.isFeatureEnabled(
      "ffSoftRequiredMobileNumber"
    );
    if (phoneSoftRequired && this.userForm.get("mobileNumber").value === null) {
      const alert = this.alertCtrl.create({
        title: "Opret uden telefonnummer?",
        message:
          "Hvis du opretter patienten uden telefonnummer, er det ikke muligt at sende SMS-påmindelser til patienten, medmindre telefonummeret tilføjes senere.",
        buttons: [
          { text: "Annullér", role: "cancel", handler: () => {} },
          {
            text: "Opret patient",
            handler: () => {
              if (this.modalType === "CREATE") {
                this.createPatient();
              } else {
                this.patchPatient();
              }
            }
          }
        ]
      });
      alert.present();
    } else {
      if (this.modalType === "CREATE") {
        this.createPatient();
      } else {
        this.patchPatient();
      }
    }
  }

  cprLookup() {
    const ssn = this.userForm.get("ssn").value;
    const loading = this.loadingCtrl.create({
      content: "Laver CPR opslag...",
      dismissOnPageChange: true
    });
    loading.present();
    this.admin.cprLookup(ssn, "Patient creation").subscribe(
      result => {
        this.handleCprLookupResult(result);
        loading.dismiss();
      },
      err => {
        loading.dismiss();
        const toast = this.toastCtrl.create({
          message: `CPR-opslag fejlede af ukendte årsager`,
          position: "top",
          closeButtonText: "Luk",
          duration: 5000,
          showCloseButton: true
        });
        toast.present();
      }
    );
  }

  handleCprLookupResult(result: ICprLookupResult) {
    if (!result.success) {
      const errorAlert = this.alertCtrl.create({
        title: "CPR opslag fejlede",
        message: `${result.error_msg} (fejlkode: ${result.error_code})`,
        buttons: ["OK"]
      });
      errorAlert.present();
      return;
    } else {
      this.userForm.patchValue(result.data);
      const toast = this.toastCtrl.create({
        message: `Navn og adresse fra CPR-registeret er indsat`,
        position: "top",
        closeButtonText: "Luk",
        duration: 5000,
        showCloseButton: true
      });
      toast.present();
    }
  }
}



// WEBPACK FOOTER //
// ./src/modals/manage-patient.modal/manage-patient.modal.ts